import {
    Directive,
    ElementRef,
    EventEmitter,
    Inject,
    Injectable,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SecurityContext,
    SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observer, PartialObserver, Subject, Subscriber } from 'rxjs';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';

export class IFrameSubscriber<T> extends Subscriber<T> {
    iframeId: string;

    constructor(
        iframeId: string,
        destinationOrNext?: PartialObserver<any> | ((value: T) => void),
        error?: (e?: any) => void,
        complete?: () => void,
    ) {
        super(destinationOrNext, error, complete);
        this.iframeId = iframeId;
        console.log('iframeId=%s', this.iframeId);
    }
}

@Injectable()
export class IFrameService {
    private readonly _onMessageEventListener: EventListener;
    private readonly _iframeStack: IFrameDirective[];
    messages: Subject<any>;

    constructor(
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._onMessageEventListener = (event) => this._onMessage(event);
        this._iframeStack = [];
        this.messages = new Subject();
        window.addEventListener('message', this._onMessageEventListener);
    }

    iframe(id?: string, href?: string): IFrameDirective {
        let iframe;
        if (id != null || href != null) {
            // eslint-disable-next-line array-callback-return
            iframe = this._iframeStack.find((value) => {
                if (id != null) {
                    return id === value.id;
                }
                if (href != null) {
                    return href === value.newSrc;
                }
            });
        }
        if ((id != null && iframe == null) || iframe != null) {
            return iframe;
        }
        iframe = this._iframeStack.find(
            (value) => document.activeElement === value.el.nativeElement,
        );
        return (
            iframe ||
            (this._iframeStack.length !== 0 ? this._iframeStack[0] : null)
        );
    }

    isRegistered(iframe: IFrameDirective): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        return this._iframeStack.indexOf(iframe) !== -1;
    }

    register(iframe: IFrameDirective): void {
        this._iframeStack.unshift(iframe);
    }

    unRegister(iframe: IFrameDirective): void {
        if (!this.isRegistered(iframe)) return;
        this._iframeStack.splice(this._iframeStack.indexOf(iframe), 1);
    }

    private _onMessage(event: any): void {
        const _event = event.originalEvent || event;
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        if (this._appConfig.IFRAME_ORIGINS.indexOf(_event.origin) === -1)
            return;
        console.log(_event.data);
        const iframe = this.iframe(event.data.iframeId, event.data.href);
        if (!iframe) return;
        try {
            if (event.data.event && event.data.event === 'DOMContentLoaded') {
                iframe.connected = true;
                while (iframe.pendingMessages.length) {
                    this.postMessage(iframe, iframe.pendingMessages.shift());
                }
                console.log('connected: origin=%s', event.origin, iframe);
            }
            if (event.data.event && event.data.event === 'unload') {
                iframe.connected = false;
                console.log('disconnected: origin=%s', event.origin, iframe);
            }
        } catch (err) {}
        this._handleMessage(iframe, _event.data);
    }

    protected _handleMessage(iframe: IFrameDirective, msg: any): void {
        this._filterObservers(iframe.id, this.messages.observers).forEach(
            (obsr: IFrameSubscriber<any>) => obsr.next(msg),
        );
    }

    private _filterObservers(
        iframeId: string,
        observers: Array<Observer<any>>,
    ): Array<Observer<any>> {
        return observers.filter(
            (obsr: IFrameSubscriber<string>) => obsr.iframeId === iframeId,
        );
    }

    postMessage(iframe: IFrameDirective, msg: any): void {
        msg = msg || {};
        msg.iframeId = iframe.id;
        try {
            if (iframe.connected) {
                this._appConfig.IFRAME_ORIGINS.forEach((origin) => {
                    try {
                        iframe.el.nativeElement.contentWindow.postMessage(
                            msg,
                            origin,
                        );
                    } catch (err) {}
                });
            } else {
                iframe.pendingMessages.push(msg);
            }
        } catch (err) {}
    }
}

@Directive({
    selector: 'iframe[cmps-iframe]',
    exportAs: 'cmpsIframe',
})
export class IFrameDirective implements OnInit, OnChanges, OnDestroy {
    @Input()
    id: string;

    @Output()
    idChange: EventEmitter<string>;

    @Input()
    newSrc: string;

    @Input()
    hasComm: boolean;

    @Output()
    onload: EventEmitter<void>;

    @Output()
    unload: EventEmitter<void>;

    private _unload: EventListener;
    connected: boolean;
    pendingMessages: any[];

    constructor(
        public el: ElementRef,
        protected _sanitizer: DomSanitizer,
        protected _service: IFrameService,
    ) {
        this.idChange = new EventEmitter<string>();
        this.onload = new EventEmitter<void>();
        this.unload = new EventEmitter<void>();
        this.pendingMessages = [];
    }

    ngOnInit(): void {
        this.id = `comm${(Math.random() * 100000).toFixed()}`;
        this.idChange.next(this.id);
        this.el.nativeElement.onload = () => {
            console.log('nativeElement onload');
            this.onload.emit();
        };
        this._unload = (event) => {
            console.log('nativeElement unload');
            this.unload.emit();
        };
        this.el.nativeElement.contentWindow.addEventListener(
            'unload',
            this._unload,
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.hasComm && !this._service.isRegistered(this)) {
            this._service.register(this);
        }

        if (!this.hasComm && this._service.isRegistered(this)) {
            this._service.unRegister(this);
        }

        this._goTo();
    }

    ngOnDestroy(): void {
        this._service.unRegister(this);
        try {
            this.el.nativeElement.contentWindow.removeEventListener(
                'unload',
                this._unload,
            );
        } catch (err) {}
        this.el.nativeElement.onload = null;
    }

    private _goTo(): void {
        console.log(this.newSrc);
        if (!this.newSrc) return;

        // nth assignments to src push to history
        if (!this.connected) {
            this.el.nativeElement.src = this._sanitizer.sanitize(
                SecurityContext.URL,
                this.newSrc,
            );
        } else {
            this._service.postMessage(this, {
                method: 'replaceLocationUrl',
                url: this.newSrc,
            });
        }
    }
}
